import type { StorageQuotaResponse } from '../../lib/api/storage';
import { formatBytes, isUnlimited, formatStorageLimit } from '../../utils/formatters';

interface StorageQuotaFooterProps {
  quota: StorageQuotaResponse | undefined;
  isLoading: boolean;
}

export function StorageQuotaFooter({ quota, isLoading }: StorageQuotaFooterProps) {
  if (isLoading || !quota) {
    return (
      <div className="px-6 py-3 border-t border-zinc-200 dark:border-zinc-700 flex items-center gap-4">
        <div className="h-3 bg-zinc-200 dark:bg-zinc-700 rounded-sm animate-pulse w-48" />
      </div>
    );
  }

  const unlimited = isUnlimited(quota.limitBytes);
  const usagePercent =
    !unlimited && quota.limitBytes > 0 ? (quota.usedBytes / quota.limitBytes) * 100 : 0;
  const barColor = (() => {
    if (unlimited) return 'bg-green-500';
    if (usagePercent > 90) return 'bg-red-500';
    if (usagePercent > 75) return 'bg-yellow-500';
    return 'bg-green-500';
  })();

  return (
    <div className="px-6 py-3 border-t border-zinc-200 dark:border-zinc-700 flex justify-between items-center">
      <div className="flex items-center gap-4">
        <span className="text-xs text-zinc-500 dark:text-zinc-400">Storage:</span>
        <div
          role="progressbar"
          aria-valuenow={Math.round(usagePercent)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Storage usage: ${Math.round(usagePercent)}%`}
          className="w-48 bg-zinc-200 dark:bg-zinc-700 rounded-sm h-1.5 overflow-hidden"
        >
          <div
            className={`h-full rounded-sm transition-all ${barColor}`}
            style={{ width: unlimited ? '100%' : `${Math.min(usagePercent, 100)}%` }}
          />
        </div>
        <span className="text-xs text-zinc-500 dark:text-zinc-400">
          {formatBytes(quota.usedBytes, '0 B')} / {formatStorageLimit(quota.limitBytes)} used
        </span>
      </div>
      <span className="text-xs text-zinc-500 dark:text-zinc-400">
        {unlimited ? 'Unlimited' : `${formatBytes(quota.availableBytes, '0 B')} available`}
      </span>
    </div>
  );
}
